import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { Button } from '@ps/web-shared-components';
import JSON5 from 'json5';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CMSHeading } from '@swf/cms';
import HTMLText from '@swf/components/HTMLText/HTMLText';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import useTemplateContent from '@swf/core/hooks/useTemplateContent';
import { type Dispatch } from '@swf/core/store';
import type { PSNPartnerNotification } from '@swf/core/types/Notification';

import NotificationContainer from '../NotificationContainer/NotificationContainer';

import { TXTCLI_LoadingXX } from './PartnerNotification.messages.json';
import styles from './PartnerNotification.scss';

const PartnerNotification: React.FC = () => {
  const { formatMessage } = useIntl();
  const isEmebedded = useSelector(select.device.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const searchParams = useSearchParams<PSNPartnerNotification>();
  const windowPayloadParams =
    canUseDOM && isEmebedded && (window.starsweb?.payload as PSNPartnerNotification);
  const iframePayloadParams =
    canUseDOM && embeddedRoute && (window.parent.starsweb?.payload as PSNPartnerNotification);
  const payloadParams = windowPayloadParams || iframePayloadParams;
  if (payloadParams && typeof payloadParams.templateContext === 'string') {
    payloadParams.templateContext = JSON5.parse(payloadParams.templateContext);
  }
  const notification = payloadParams || searchParams;
  const { content, title, actions, loading, error } = useTemplateContent(notification);
  const { modal, notification: notificationDispatch } = useDispatch<Dispatch>();
  const psnPartnerNotificationChannel = useRef(
    new BroadcastChannel('psn-partner-notification-modal')
  );

  useEffect(() => {
    if (payloadParams) {
      notificationDispatch.addPSNPartnerNotification(notification);
    }
  }, [notification, notificationDispatch, payloadParams]);

  const handleClose = async () => {
    await notificationDispatch.aknowledgePSNPartnerNotification(notification);
    if (isEmebedded) {
      window.close();
    } else if (embeddedRoute) {
      window.parent.postMessage('close');
    } else {
      modal.unqueuePSNPartnerNotification({ path: '/notifications/psn-partner', notification });
      psnPartnerNotificationChannel.current.postMessage(notification);
    }
  };

  if (loading)
    return (
      <NotificationContainer hideCloseButton>
        {formatMessage(TXTCLI_LoadingXX)}
      </NotificationContainer>
    );
  if (error)
    return (
      <NotificationContainer styledNotification>
        {(error as Error).message || error.toString()}
      </NotificationContainer>
    );

  return (
    <NotificationContainer title={title} onClose={handleClose} hideCloseButton>
      {isEmebedded && <CMSHeading level="h1" title={title?.trim()} alignment="left" hero={false} />}
      <HTMLText className={styles.content} html={content?.trim()} />
      <div className={styles.buttonContainer}>
        {actions?.map((action) => (
          <Button className={styles.button} onClick={handleClose} key={action.id}>
            {action.label.trim()}
          </Button>
        ))}
      </div>
    </NotificationContainer>
  );
};

export default PartnerNotification;
